import React, { FC, useEffect, useState } from 'react';
import { formattedProviderData } from '../../store/tables/admin/provider-data';
import { Title } from '../developers/page-components/Title';
import { ProviderCheckbox } from './ProviderCheckbox';
import { ProviderTableInput } from './ProviderTableInput';
import { ProviderTableMultiSelector } from './ProviderTableMultiSelector';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../merchants/RelatedEntityNameLink';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { ILists } from '../../interfaces/merchant';
import { useFormik } from 'formik';
import {
	convertToInitialProviderDetailsValues,
	getProviderDetailsValidationSchema, PROVIDER_DETAILS_FORM_FIELDS, PROVIDER_DETAILS_FORM_FIELDS_NAMES,
	providerDetailsFormInitialValues,
} from '../../constants/provider/provider-details-form';
import { TOTAL_INPUTS } from '../../constants/provider/provider-details-total-inputs';
import { MetricInputComponent } from './MetricProviderComponent';
import { ISelectorData } from '../application/NewApplication';
import { usePaymentContext } from '../../store/payments/payments-context';
import {
	fetchProviderData,
	fetchProviderLists,
	setProvidersV2,
} from '../../store/payments/actions/providers';
import { TableSelector } from './TableSelector';
import { useLocation } from 'react-router';
import { fetchMccList } from '../../store/merchnts/actions/merchant';
import { useTranslation } from 'react-i18next';
import { ProviderDetailsHeader } from './ProviderDetailsHeader';
import { IProviderFiltersProps } from './ProviderFilters';
import { parseSearchParams } from '../../utils/utilities';
import { IReleaseState } from '../../routing/routing-helper/provider-navigation';
import { InputType } from '../form/form-input-constants';

const fields = [
	// {
	// 	title: 'Provider',
	// 	fieldName: 'name',
	// 	canChange: true,
	// },
	// {
	// 	title: 'MCC',
	// 	fieldName: 'mcc',
	// 	canChange: true,
	// },
	{
		title: 'Processor Id',
		fieldName: 'processorId',
		canChange: true,
	},
];

const generalFields = [
	{
		title: 'Merchant number',
		fieldName: 'merchantNumber',
		canChange: true,
		inputType: InputType.Number,
	},
	// {
	// 	title: 'Sponsor Bank',
	// 	fieldName: 'sponsorBank',
	// 	canChange: true,
	// },
	{
		title: 'CAID',
		fieldName: 'caid',
		canChange: true,
		inputType: InputType.Number,
	},
	{
		title: 'BIN',
		fieldName: 'bin',
		canChange: true,
		inputType: InputType.Number,
	},
	// {
	// 	title: 'Risk tier',
	// 	fieldName: 'tier',
	// 	canChange: true,
	// },
];

const getMultiSelectorFields = (lists: ILists, cardBrandsList: ISelectorData[]): any[] => [
	{
		title: 'Card Brands',
		fieldName: 'cardBrands',
		list: cardBrandsList
	},
	{
		title: 'Supported Countries',
		fieldName: 'supportedCountries',
		list: lists?.countries
	},
	{
		title: 'Supported Currencies',
		fieldName: 'supportedCurrencies',
		list: lists?.currencies
	},
];

const prefixFields = [
	{
		title: 'Fixed Descriptor Prefix',
		fieldName: 'fixedDescriptorPrefix',
		canChange: false,
	},
	{
		title: 'MC Descriptor Prefix',
		fieldName: 'masterCardDescriptorPrefix',
		canChange: true,
	},
	{
		title: 'Visa Descriptor Prefix',
		fieldName: 'visaDescriptorPrefix',
		canChange: true,
	},
];

const checkboxFields = [
	{
		title: 'MIT Support',
		fieldName: 'supportMIT',
		isDisabled: false,
	},
	{
		title: 'CIT Support',
		fieldName: 'supportCIT',
		isDisabled: false,
	},
	{
		title: 'Sandbox',
		fieldName: 'sandbox',
		isDisabled: true,
	},
];

const disputeManagementCheckboxes = [
	// {
	// 	title: 'Use dynamic descriptor',
	// 	fieldName: 'useDynamicDescriptor',
	// 	isDisabled: false,
	// 	tooltipText: 'Enable this to use dynamic descriptors, overriding the default merchant descriptor on file at the ISO/acquirer settings for transactions. Note: If this is not enabled, MC/Visa prefixes/suffixes, as well as address and URL additions, will not function.',
	// },
	{
		title: 'Support Dynamic Descriptor Url',
		fieldName: 'supportDynamicDescriptorUrl',
		isDisabled: false,
		tooltipText: 'Enable this to include the URL defined in the merchant’s site settings in dynamic transaction descriptors. Note: Requires ‘Use Dynamic Descriptor’ to be enabled.',
	},
	{
		title: 'Support Dynamic Descriptor Address',
		fieldName: 'supportDynamicDescriptorAddress',
		isDisabled: false,
		tooltipText: 'Enable this to include the address defined in the merchant’s site settings in dynamic transaction descriptors. Note: Requires ‘Use Dynamic Descriptor’ to be enabled.',
	},
	{
		title: 'Use Dynamic Descriptor Suffix',
		fieldName: 'useDynamicDescriptorSuffix',
		isDisabled: false,
		tooltipText: 'Enable this to add dynamic suffixes to transaction descriptors for more detailed information. Note: Requires ‘Use Dynamic Descriptor’ to be enabled.',
	},
];




const disputesFields = [
	{
		title: 'Ethoca Provider',
		fieldName: 'ethocaProvider',
		canChange: false,
	},
	{
		title: 'Rdr Provider',
		fieldName: 'rdrProvider',
		canChange: false,
	},
	{
		title: 'Chargebacks Provider',
		fieldName: 'chargebacksManagementProvider',
		canChange: false,
	},
];

const contactFields = [
	{
		title: 'Name',
		fieldName: 'contactName',
		canChange: true,
		inputType: 'string',
	},
	{
		title: 'Email',
		fieldName: 'contactEmail',
		canChange: true,
		inputType: 'string',
	},
	{
		title: 'Phone',
		fieldName: 'contactPhone',
		canChange: true,
		inputType: 'string',
	},
];

export const tierMockList = [
	{
		label: 'Tier 1',
		value: '1',
	},
	{
		label: 'Tier 2',
		value: '2',
	},
	{
		label: 'Tier 3',
		value: '3',
	},
];

interface Props {
	setReleaseState: (state: IReleaseState) => void;
}
export const ProviderDetails: FC<Props> = ({ setReleaseState }) => {
	const { search } = useLocation();
	const { t } = useTranslation();

	const { merchantState: { merchantsLists, mccList }, dispatch: merchantDispatch } = useMerchants();
  const { paymentContextState, dispatch } = usePaymentContext();
	const { providerData, providerLists, isProviderListsLoading, isProviderDataLoading } = paymentContextState;


	const searchParams = new URLSearchParams(search);
	const providerId = searchParams.get('providerId');
	const filteredProvider = providerData?.find(el => el?.id === providerId);
	const data = formattedProviderData([filteredProvider])[0];

	console.log('provider details data', data);


	const [useDynamicDescriptor, setUseDynamicDescriptor] = useState<boolean>(data?.useDynamicDescriptor);
	const [loading, setLoading] = useState<boolean>(false);

	const filtersFromUrl: IProviderFiltersProps = parseSearchParams(search);

	const formik = useFormik({
		initialValues: providerDetailsFormInitialValues,
		validationSchema: getProviderDetailsValidationSchema(t),
		onSubmit: () => void 0,
	});

	useEffect(() => {
		if (data) {
			const convertedData = convertToInitialProviderDetailsValues(data);
			formik.setValues(convertedData);
		}
	}, [providerData]);

	useEffect(() => {
		void fetchProviderLists({ dispatch, providerName: 'nmi' });
		void fetchMccList(merchantDispatch);
	}, []);

	const METRIC_INPUTS = TOTAL_INPUTS(formik);

	const handleSave = async (fieldName: string): Promise<void> => {
		try {
			setLoading(true);
			let params;
			if (fieldName === PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_COUNT
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_VALUE
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_COUNT
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_VALUE
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_COUNT
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_VALUE
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_COUNT
				|| fieldName === PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_VALUE
			) {
				params = {
					id: data?.id,
					capabilities: {
						[fieldName]: data?.[fieldName]
					}
				};

			} else {
				params = { id: data?.id, [fieldName]: data?.[fieldName] };
			}
			switch (fieldName) {
				case PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_COUNT:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_COUNT] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_COUNT]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_COUNT])
						: -1;
					break;
				case PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_VALUE:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_VALUE] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_VALUE]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.MC_MONTHLY_MAX_TRANSACTIONS_VALUE]) * 100
						: -1;
					break;
				case PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_COUNT:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_COUNT] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_COUNT]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_COUNT])
						: -1;
					break;
				case PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_VALUE:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_VALUE] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_VALUE]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_MONTHLY_MAX_TRANSACTIONS_VALUE]) * 100
						: -1;
					break;
					case PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_COUNT:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_COUNT] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_COUNT]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_COUNT])
						: -1;
					break;
				case PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_VALUE:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_VALUE] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_VALUE]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.TOTAL_DAILY_MAX_TRANSACTIONS_VALUE]) * 100
						: -1;
					break;
				case PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_COUNT:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_COUNT] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_COUNT]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_COUNT])
						: -1;
					break;
				case PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_VALUE:
					params.capabilities[PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_VALUE] = formik.values[PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_VALUE]
						? Number(formik.values[PROVIDER_DETAILS_FORM_FIELDS.VISA_MONTHLY_MAX_TRANSACTIONS_VALUE]) * 100
						: -1;
					break;
				default:
					params[fieldName] = formik.values[fieldName];
					break;
			}

			await setProvidersV2({ dispatch, params, formik });
			await fetchProviderData({ dispatch, searchParams: filtersFromUrl });

			setLoading(false);
		} catch (e) {
			console.error('handleSave error', e);
			setLoading(false);
		}
	};

	const handleDataSave = async (fieldName: string, value: string): Promise<void> => {
		try {
			const params = {
				id: data?.id,
				[fieldName]: value || '',
			};

			await setProvidersV2({ dispatch, params, formik });
			await fetchProviderData({ dispatch, searchParams: filtersFromUrl });
		} catch (e) {
			console.error('handleSelect error', e);
		}
	};

	return (
		<div className="px-8">
			<div className="row">

				<ProviderDetailsHeader data={data} setReleaseState={setReleaseState} />

				<div className="separator mb-5" />

				<Title title="Processing configurations" additionalClass="fs-5 fw-bolder mb-5" />

				<div className="col-6 col-md-3 mb-5">
					<div className="d-flex align-items-center">
						<Title title={PROVIDER_DETAILS_FORM_FIELDS_NAMES.PROVIDER_NAME} additionalClass="fs-7 text-muted" />
					</div>
					<ProviderTableInput
						formik={formik}
						fieldName={PROVIDER_DETAILS_FORM_FIELDS.PROVIDER_NAME}
						initialValue={data?.[PROVIDER_DETAILS_FORM_FIELDS.PROVIDER_NAME]}
						inputType="string"
						onConfirm={handleDataSave}
					/>
				</div>

				<div className="col-3">
					<Title title="Processor Platform" additionalClass="fs-7 text-muted" />
					<TableSelector
						formik={formik}
						title={PROVIDER_DETAILS_FORM_FIELDS_NAMES.PROCESSOR_PLATFORM}
						fieldName={PROVIDER_DETAILS_FORM_FIELDS.PROCESSOR_PLATFORM}
						list={providerLists?.ProcessorPlatforms}
						onSelect={handleDataSave}
						disabled={isProviderListsLoading || isProviderDataLoading}
					/>
				</div>
				<div className="col-6 col-md-3 mb-5">
					<div className="d-flex align-items-center">
						<Title title={PROVIDER_DETAILS_FORM_FIELDS_NAMES.SPONSOR_BANK} additionalClass="fs-7 text-muted" />
					</div>
					<ProviderTableInput
						formik={formik}
						fieldName={PROVIDER_DETAILS_FORM_FIELDS.SPONSOR_BANK}
						initialValue={data?.[PROVIDER_DETAILS_FORM_FIELDS.SPONSOR_BANK]}
						inputType="string"
						onConfirm={handleDataSave}
					/>
				</div>
				<div className="col-3">
					<Title title="MCC" additionalClass="fs-7 text-muted" />
					<TableSelector
						formik={formik}
						title={PROVIDER_DETAILS_FORM_FIELDS_NAMES.MCC}
						fieldName={PROVIDER_DETAILS_FORM_FIELDS.MCC}
						list={mccList}
						onSelect={handleDataSave}
						disabled={isProviderListsLoading || isProviderDataLoading}
						searchable={true}
					/>
				</div>

				{fields.map(({ title, fieldName, canChange }) => (
					<div className="col-6 col-md-3 mb-5" key={fieldName}>
						<div className="d-flex align-items-center">
							<Title title={title} additionalClass="fs-7 text-muted" />
						</div>
						{canChange
							? <ProviderTableInput
								formik={formik}
								fieldName={fieldName}
								initialValue={data?.[fieldName]}
								inputType="string"
								onConfirm={handleDataSave}
							/>
							: <div>{data?.[fieldName] ? data?.[fieldName] : '-'}</div>
						}
					</div>
				))}

				{
					getMultiSelectorFields(merchantsLists, providerLists?.CardBrand).map(({ title, fieldName, list }) => (
						<div className="col-6 col-md-3 mb-5" key={fieldName}>
							<div className="d-flex align-items-center">
								<Title title={title} additionalClass="fs-7 text-muted" />
							</div>
							<ProviderTableMultiSelector
								title={title}
								value={data?.[fieldName]}
								id={data?.id}
								fieldName={fieldName}
								list={list}
							/>
						</div>
					))
				}

				{/*<div className="col-md-3" />*/}

				{checkboxFields.map(({ title, fieldName, isDisabled }) => {
					return (
						<ProviderCheckbox
							key={fieldName}
							value={data?.[fieldName]}
							id={data?.id}
							fieldName={fieldName}
							isDisabled={isDisabled}
							label={title}
						/>
					);
				})}

				<div className="separator mb-5" />

				<Title title="Disputes management" additionalClass="fs-5 fw-bolder mb-5" />

				{disputesFields.map(({ title, fieldName, canChange }) => (
					<div className="col-6 col-md-3 mb-5" key={fieldName}>
						<div className="d-flex align-items-center">
							<Title title={title} additionalClass="fs-7 text-muted" />
						</div>
						{canChange
							? <ProviderTableInput
								formik={formik}
								fieldName={fieldName}
								initialValue={data?.[fieldName]}
								inputType="string"
								onConfirm={handleDataSave}
							/>
							: <div>{data?.[fieldName] ? data?.[fieldName] : '-'}</div>
						}
					</div>
				))}

				<div className="col-md-3" />

				{prefixFields.map(({ title, fieldName, canChange }) => (
					<div className="col-6 col-md-3 mb-5" key={fieldName}>
						<div className="d-flex align-items-center">
							<Title title={title} additionalClass="fs-7 text-muted" />
						</div>
						{canChange
							? <ProviderTableInput
								formik={formik}
								fieldName={fieldName}
								initialValue={data?.[fieldName]}
								inputType="string"
								onConfirm={handleDataSave}
							/>
							: <div>{data?.[fieldName] ? data?.[fieldName] : '-'}</div>
						}
					</div>
				))}

				<div className="col-md-3" />

				<ProviderCheckbox
					value={useDynamicDescriptor}
					setValue={setUseDynamicDescriptor}
					id={data?.id}
					fieldName="useDynamicDescriptor"
					isDisabled={false}
					label="Use Dynamic Descriptor"
					tooltipText="Enable this to use dynamic descriptors, overriding the default merchant descriptor on file at the ISO/acquirer settings for transactions. Note: If this is not enabled, MC/Visa prefixes/suffixes, as well as address and URL additions, will not function."
					additionalContainerClass="col-md-12"
				/>

				{
					useDynamicDescriptor && (
						disputeManagementCheckboxes.map(({ title, fieldName, isDisabled, tooltipText }) => {
							return (
								<ProviderCheckbox
									key={fieldName}
									value={data?.[fieldName]}
									id={data?.id}
									fieldName={fieldName}
									isDisabled={isDisabled}
									label={title}
									tooltipText={tooltipText}
									additionalContainerClass="col-md-4"
								/>
							);
						})
					)
				}

				<div className="separator mb-5" />

				<Title title="MerchantGeneralConfiguration configurations" additionalClass="fs-5 fw-bolder mb-5" />

				<div className="col-6 col-md-3 mb-5">
					<Title title="Partner" additionalClass="fs-7 text-muted" />
					<RelatedEntityNameLink id={data?.partnerId} type={RELATED_ENTITY_TYPE.PARTNER} />

				</div>

				{generalFields.map(({ title, fieldName, canChange, inputType }) => (
					<div className="col-6 col-md-3 mb-5" key={fieldName}>
						<div className="d-flex align-items-center">
							<Title title={title} additionalClass="fs-7 text-muted" />
						</div>
						{canChange
							? <ProviderTableInput
								formik={formik}
								fieldName={fieldName}
								initialValue={data?.[fieldName]}
								inputType={inputType}
								onConfirm={handleDataSave}
							/>
							: <div>{data?.[fieldName] ? data?.[fieldName] : '-'}</div>
						}
					</div>
				))}

				<div className="col-3 mb-5">
					<Title title={PROVIDER_DETAILS_FORM_FIELDS_NAMES.RISK_TIER} additionalClass="fs-7 text-muted" />
					<TableSelector
						formik={formik}
						title={PROVIDER_DETAILS_FORM_FIELDS_NAMES.RISK_TIER}
						fieldName={PROVIDER_DETAILS_FORM_FIELDS.RISK_TIER}
						list={tierMockList}
						onSelect={handleDataSave}
						disabled={isProviderDataLoading}
					/>
				</div>

				<ProviderCheckbox
					value={data?.supportUDF}
					id={data?.id}
					fieldName="supportUDF"
					isDisabled={false}
					label="Support UDF"
					tooltipText="Enable this to allow the use of User-Defined Fields (UDFs) in transactions for custom data tracking and enhanced reporting."
					additionalContainerClass="align-items-center"
				/>

				<div className="separator mb-5" />

				{
					METRIC_INPUTS.map(input => (
						<MetricInputComponent
							{...input}
							formik={formik}
							onConfirm={handleSave}
							loading={loading}
							key={input.key}
							providerDetailsData={data}
						/>
					))
				}

				<div className="separator my-5" />

				<Title title="MerchantContactPage Information" additionalClass="fs-5 fw-bolder mb-5" />

				{contactFields.map(({ title, fieldName, canChange, inputType }) => (
					<div className="col-6 col-md-3 mb-5" key={fieldName}>
						<Title title={title} additionalClass="fs-7 text-muted" />
						{canChange
							? <ProviderTableInput
								formik={formik}
								fieldName={fieldName}
								initialValue={data?.[fieldName]}
								inputType={inputType}
								onConfirm={handleDataSave}
							/>
							: <div>{data?.[fieldName] ? data?.[fieldName] : '-'}</div>
						}
					</div>
				))}

			</div>
		</div>
	);
};
